<template>
  <div id="app-container" :class="{ 'is-quiz-mode': isQuizRoute }">
    <AppHeader v-if="!isQuizRoute" />

    <main :class="{ 'is-quiz-mode': isQuizRoute }">
      <!-- router-view отображает компонент, соответствующий текущему URL -->
      <router-view/>
    </main>

    <footer class="app-footer" v-if="!isQuizRoute">
      <p>{{ footerContent.copyrightText }}</p>
    </footer>

    <AuthModal
      :show="showAuthModal"
      @close="handleCloseAuthModal"
      @login="handleLogin"
      @register="handleRegister"
    />
  </div>
</template>

<script>
import AppHeader from './components/AppHeader.vue';
import AuthModal from './components/AuthModal.vue';
import { ref, watch, computed } from 'vue';
import { languageStore } from './languageStore';
import { userStore } from './userStore';
import { useRoute } from 'vue-router';

export default {
  name: 'App',
  components: {
    AppHeader,
    AuthModal,
  },
  setup() {
    const currentLanguage = ref(languageStore.currentLanguage);
    const route = useRoute();
    const footerContent = ref({});
    const showAuthModal = ref(false);

    const loadFooterContent = async (lang) => {
      try {
        const module = await import(`./locales/footer/footer_${lang}.json`);
        footerContent.value = module.default;
      } catch (error) {
        console.error('Failed to load footer content:', error);
        footerContent.value = {};
      }
    };

    watch(() => route.params.lang, (newLang) => {
      if (newLang) {
        currentLanguage.value = newLang;
        languageStore.setLanguage(newLang);
        loadFooterContent(newLang);
      }
    }, { immediate: true });

    watch(() => languageStore.currentLanguage, (newLang) => {
      currentLanguage.value = newLang;
      loadFooterContent(newLang);
    });

    // Handle authentication modal events
    const handleCloseAuthModal = () => {
      showAuthModal.value = false;
    };

    const handleLogin = () => {
      userStore.login();
      showAuthModal.value = false;
      console.log('User logged in.');
      // TODO: Implement actual login logic and redirection
    };

    const handleRegister = () => {
      showAuthModal.value = false;
      console.log('Navigate to registration page.');
      // TODO: Implement actual registration logic and redirection
    };

    const currentLevelClass = computed(() => {
      const levelId = route.params.levelId;
      return levelId ? `level-${levelId}` : '';
    });

    watch(currentLevelClass, (newClass, oldClass) => {
      const body = document.body;
      if (oldClass) {
        body.classList.remove(oldClass);
      }
      if (newClass) {
        body.classList.add(newClass);
      }
    }, { immediate: true });

    // Strict Rule 13: All quiz and grammar trainer routes run in focus mode without site header and footer
    const QUIZ_ROUTE_NAMES = [
      'SimpleChoiceQuiz',
      'NewMasterQuiz',
      'UniversalQuizEngine',
      'ModularQuizEngine',
      'SentenceBuilderQuiz',
      'ModularSentenceQuiz',
      'TopologicalSentenceQuiz'
    ];

    const QUIZ_PATH_PATTERNS = [
      '/quizzes',
      '/new-quizzes',
      '/universal-quizzes',
      '/modular-quizzes',
      '/sentence-builder',
      '/modular-sentence',
      '/topological-sentence'
    ];

    const isQuizRoute = computed(() => {
      if (route.meta && (route.meta.hideHeaderFooter || route.meta.isQuiz)) {
        return true;
      }
      if (route.name && QUIZ_ROUTE_NAMES.includes(route.name)) {
        return true;
      }
      if (route.path) {
        return QUIZ_PATH_PATTERNS.some(pattern => route.path.includes(pattern));
      }
      return false;
    });

    watch(isQuizRoute, (isQuiz) => {
      const body = document.body;
      if (isQuiz) {
        body.classList.add('is-quiz-mode');
      } else {
        body.classList.remove('is-quiz-mode');
      }
    }, { immediate: true });

    return {
      footerContent,
      showAuthModal,
      isQuizRoute,
      handleCloseAuthModal,
      handleLogin,
      handleRegister,
    };
  },
};
</script>

<style>

/* Здесь можно добавить глобальные стили */
#app-container {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  max-width: 900px;
  margin: 0 auto;
  padding: 20px;
  margin-top: 60px;
}

/* Full-Screen Distraction-Free Focus Mode for Quizzes & Trainers (Rule 13) */
body.is-quiz-mode {
  margin: 0 !important;
  padding: 0 !important;
  background-color: #f8fafc;
}

#app-container.is-quiz-mode {
  margin: 0 !important;
  margin-top: 0 !important;
  padding: 0 !important;
  max-width: 100% !important;
  width: 100% !important;
  min-height: 100vh !important;
}

#app-container.is-quiz-mode main,
main.is-quiz-mode {
  margin: 0 !important;
  margin-top: 0 !important;
  padding: 0 !important;
  max-width: 100% !important;
  width: 100% !important;
}

/* Legacy fallback selectors for specific runner containers */
body:has(.simple-choice-runner) #app-container,
#app-container:has(.simple-choice-runner),
body:has(.new-master-quiz-runner) #app-container,
#app-container:has(.new-master-quiz-runner),
body:has(.universal-quiz-runner) #app-container,
#app-container:has(.universal-quiz-runner),
body:has(.modular-quiz-container) #app-container,
#app-container:has(.modular-quiz-container),
body:has(.sentence-builder-runner-wrapper) #app-container,
#app-container:has(.sentence-builder-runner-wrapper),
body:has(.modular-sentence-runner-wrapper) #app-container,
#app-container:has(.modular-sentence-runner-wrapper),
body:has(.topological-sentence-runner-wrapper) #app-container,
#app-container:has(.topological-sentence-runner-wrapper) {
  margin: 0 !important;
  margin-top: 0 !important;
  padding: 0 !important;
  max-width: 100% !important;
  width: 100% !important;
}

#app-container:has(.modular-quiz-container) main,
#app-container:has(.sentence-builder-runner-wrapper) main,
#app-container:has(.modular-sentence-runner-wrapper) main,
#app-container:has(.topological-sentence-runner-wrapper) main {
  margin: 0 !important;
  margin-top: 0 !important;
  padding: 0 !important;
  max-width: 100% !important;
  width: 100% !important;
}

nav {
  padding: 30px; 
}

nav a {
  font-weight: bold;
  color: #2c3e50;
}

nav a.router-link-exact-active {
  color: #42b983;
}

/* Level background colors */
body.level-a1 {
  background-color: #f6feff; /* Lighter Blue */
}
body.level-a2 {
  background-color: #fffef6; /* Lighter Yellow (almost white) */
}
body.level-b1 {
  background-color: #f8fff9; /* Lighter Green */
}
body.level-b2 {
  background-color: #fff9fb; /* Lighter Pink (almost white) */
}

main {
  margin-top: 20px;
}

.app-footer {
  text-align: center;
  padding: 1rem;
  background-color: #f8f8f8;
  margin-top: 50px;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
  padding: 20px;
  background-color: #f8f8f8;
  color: #555;
  font-size: 0.9em;
}

@media (max-width: 768px) {
  #app-container {
    margin-top: 0;
    padding-left: 0;
    padding-right: 0;
    padding-top: 0;
    padding-bottom: 1rem;
    max-width: 100%;
  }

  main {
    margin-top: 0.5rem;
  }
}
</style>